import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { getShopifySessionToken } from './shopify-app-bridge';

const BASE_URL: string =
  import.meta.env.VITE_BRILIO_API_BASE_URL || 'http://localhost:8000';

/**
 * Axios instance for talking to the Brilio API.
 *
 * Every request attaches a fresh Shopify session token (a short-lived JWT issued
 * by App Bridge). Backend exchanges it for an internal tenant-scoped identity.
 *
 * We intentionally do NOT cache the token: App Bridge tokens are short-lived
 * (~1 min) and the SDK already memoizes them under the hood.
 */
export const brilioApi: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 30_000,
});

brilioApi.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  const token = await getShopifySessionToken();
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  return config;
});

brilioApi.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    // 401 means the session token was rejected — usually clock drift or wrong API key.
    // We surface to the UI; App Bridge will normally re-issue a fresh token on next call.
    if (error.response?.status === 401) {
      console.warn('[brilio] 401 from API; will retry with fresh token on next call.');
    }
    return Promise.reject(error);
  }
);
